import { useMemo } from 'react';
import { useConceptAngles } from './use-concepts';
import { useRendersForConcept } from '@/features/design/render/model/renders-cache';
import { ConceptAngleType, RenderStatus } from '@/shared/constants';

/**
 * True when the concept's MAIN angle has at least one completed render — the
 * exact server-side precondition for album and estimate generation
 * (Concept::hasCompletedMainRender). The album/estimate Generate CTAs are
 * disabled until this turns true: a brand-new user must be funneled through
 * the free design step first, never into a paywall (the server 422s these
 * requests with `main_render_required` anyway — the disabled state just stops
 * the dead click). Computed from the realtime-synced renders cache so the
 * CTAs unlock the moment the first render completes, without a concept
 * refetch. Mirrored on Apple/Android.
 */
export function useHasCompletedMainRender(conceptId: string): boolean {
  const { data: conceptAnglesResponse } = useConceptAngles(conceptId);
  const conceptAngles = conceptAnglesResponse?.data;
  const rendersForConcept = useRendersForConcept(conceptId);

  return useMemo(() => {
    const mainAngle = conceptAngles?.find(
      (ca) => ca.attributes.type === ConceptAngleType.MAIN
    );
    if (!mainAngle) return false;
    return rendersForConcept.some(
      (r) =>
        r.relationships?.concept_angle?.data?.id === mainAngle.id &&
        r.attributes.status === RenderStatus.COMPLETED
    );
  }, [conceptAngles, rendersForConcept]);
}
